import type { UniformTabKey } from '../../services/api';

/**
 * Dashboard → Uniform & Asset Management: the shapes the API sends and the pure
 * rules the screen applies to them. No React and no network in here, so every rule
 * below is unit-tested on its own (uniformDashboard.test.ts) and the tab file stays
 * about layout and loading.
 *
 * The screen is a port of 1Office Project Management's "Uniform Report", which is
 * itself a mirror of Admin Management's Uniforms → Dashboard. The wording and the
 * judgement calls (what counts as Within SLA, when a kit line is "not drawn") are
 * Admin Management's — they are repeated here so a request reads the same on all
 * three screens, not re-decided.
 *
 * Two rules run through the whole file:
 *   1. Dates are TEXT. Admin Management sends 'YYYY-MM-DD' in India time; pushing
 *      one through `new Date()` parses it as UTC midnight and shows the previous
 *      day to anyone west of Greenwich. Every formatter here works on the string.
 *   2. A missing number is not zero. "Not measured", "not loaded", "failed" and "not
 *      sent" (the API passes on null where Admin Management gave nothing readable)
 *      each render as a dash or an ellipsis — a 0 would read as a real count, and on
 *      a kit line as the accusation that a garment was never drawn from stock.
 */

export type { UniformTabKey };

/** One sub-tab's count, split into Pre Joinee (type -11, a candidate) and Joined
 *  (type 11, an employee). These are true totals; the rows are capped upstream. */
export interface UniformTabCount {
  pre_joinee: number;
  joined: number;
  total: number;
}

export type UniformCounts = Record<UniformTabKey, UniformTabCount>;

/** The 3-day TAT label Admin Management judges per row. */
export type UniformTat = 'overdue' | 'within' | 'without';

export interface UniformRequestRow {
  request_id: number;
  request_code: string;
  /** 11 Joined (employee), -11 Pre Joinee (candidate). */
  request_type: number;
  request_status: number;
  /** Completed tab only: Completed / Cancelled / Rejected by Approver / Rejected by
   *  Admin. An Admin Management build older than its Status column sends none. */
  status_label?: string | null;
  tab: UniformTabKey;
  /** Pre Joinee only. */
  join_id: number | null;
  /** Employee code, or the Join ID for a candidate. */
  person_code: string | null;
  person_name: string | null;
  project_id: number | null;
  project_name: string | null;
  budget_name: string | null;
  created_on: string | null;
  /** "User Submitted". */
  submitted_on: string | null;
  /** "Request Processed (On)"; for a cancelled / rejected request, the day it ended. */
  processed_on: string | null;
  /** Where the 3-day TAT counts from. */
  sla_start_on: string | null;
  sla_basis: 'pm_approval' | 'user_submitted' | null;
  tat: UniformTat | null;
  tat_days: number | null;
  /** The Pre Joinee kit name, for View kit's header line. */
  offer_kit_name: string | null;
}

/** One sub-tab's answer from GET /uniform-dashboard/:projectId/requests. */
export interface UniformRequestsData {
  tab: UniformTabKey;
  counts: UniformCounts;
  rows: UniformRequestRow[];
  /** Rows loaded per half upstream (the open tabs together / Completed). */
  cap: number;
  capped: { open: boolean; completed: boolean };
  /** ISO instant the API fetched this snapshot from Admin Management. */
  fetchedAt: string | null;
}

/** This India-time month's SLA counts. `measured: false` = before the KPI's go-live:
 *  there are NO counts, which is not the same as zero. */
export interface UniformSla {
  today?: string;
  /** The KPI's go-live floor. */
  since?: string;
  month?: { key?: string; label?: string; start?: string; end?: string };
  measured: boolean;
  /** The window is clipped by go-live or by today. */
  partial?: boolean;
  window?: { start?: string; end?: string };
  within?: number;
  out?: number;
  decided?: number;
  /** null = nothing decided yet this month. */
  timelyPct?: number | null;
}

/** One garment on the Kit stock card. Stock is the garment's across every
 *  warehouse, so a garment on two kits is one entry. */
export interface UniformKitStockItem {
  item_id: number;
  item_name: string;
  kit_names: string[];
  available: number;
  sizes: { size: string; available: number; reorder_level: number; status: 'out' | 'low' | 'ok' }[];
  zero_sizes: string[];
  low_sizes: string[];
}

/** The Kit stock card. `totals.low` and `totals.zero` count SIZES across all the
 *  garments, and never overlap. Each total is null when Admin Management sent
 *  nothing readable for it: the API passes that on rather than inventing a 0, and
 *  the card then shows a dash — "0 Out of stock" would be a claim nobody made. */
export interface UniformKitStock {
  kits: { kit_id: number; kit_name: string }[];
  items: UniformKitStockItem[];
  totals: { items: number | null; available: number | null; zero: number | null; low: number | null };
}

export interface UniformKitLine {
  id: number;
  item_id: number | null;
  item_name: string;
  category: string | null;
  size: string | null;
  size_status: string | null;
  quantity: number;
  /** 1 = stock already issued for this line, 0 = not issued. null = Admin Management
   *  sent nothing readable — NOT the same as 0: only a stated 0 marks the line as
   *  "not drawn" (see kitStockMark). */
  issued: number | null;
}

export interface UniformKit {
  dispatch_id: number;
  // Joined requests only — the modal's "name · kit · quarter" line.
  employee_name?: string | null;
  type_name?: string | null;
  period_label?: string | null;
  uf_mode?: string | null;
  uf_request_status?: number | null;
  items: UniformKitLine[];
}

export interface UniformIntakeLink {
  joinId: number;
  link: string;
  shortCode: string | null;
  sentOn: string | null;
  submitted: boolean;
}

export interface UniformAwb {
  sub_admin_request_id: number;
  awb_code: string;
  courier_name: string | null;
  receiver_name: string | null;
  destination_city: string | null;
  pincode: string | null;
  booking_status: string | null;
}

export interface UniformTimelineEntry {
  datetime: string;
  status: string;
  activity: string;
  location: string;
}

export interface UniformTimeline {
  awb_code: string;
  courier_name: string | null;
  current_status: string | null;
  timeline: UniformTimelineEntry[];
}

// ── Sub-tabs ─────────────────────────────────────────────────────────────────

/* The status → tab map is Admin Management's: -2 Acknowledgement Pending, 1 Approval
   Pending, 2 / 12 Courier Pending/Handover, -3 Courier In Process, 3 Completed.
   Completed also lists the cancelled (5) and rejected (6, 7) requests, told apart by
   its Status column. The order here IS the order of the strip, and the first entry
   is the one the tab opens on. `empty` is what the table says when the sub-tab
   loaded and genuinely has nothing — its true total is 0 (see emptyState). It is
   never shown for a load that failed, nor for rows that merely were not sent. */
export const UNIFORM_TABS: readonly { id: UniformTabKey; label: string; empty: string }[] = [
  { id: 'ack_pending', label: 'Acknowledgement Pending', empty: 'No request is waiting for the employee or candidate to acknowledge.' },
  { id: 'approval_pending', label: 'Approval Pending', empty: 'No request is waiting for approval.' },
  { id: 'submitted', label: 'Courier Pending/Handover', empty: 'No submitted request is waiting to be couriered or handed over.' },
  { id: 'in_process', label: 'Courier In Process', empty: 'No uniform parcel is with the courier right now.' },
  { id: 'completed', label: 'Completed', empty: 'No completed, cancelled or rejected uniform request for this project.' },
];

export const DEFAULT_UNIFORM_TAB: UniformTabKey = 'ack_pending';

const ZERO_COUNT: UniformTabCount = { pre_joinee: 0, joined: 0, total: 0 };

const toCount = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

/** A total that may honestly be missing: a whole number when the answer carries
 *  one, otherwise null. Unlike toCount it never turns "nothing readable" into 0 —
 *  on the Kit stock card a 0 is a statement ("no size is out of stock"). */
const toCountOrNull = (v: unknown): number | null => {
  // Number(null), Number('') and Number(false) are all 0, so only a real number or a
  // non-blank numeric string is read at all.
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

/** A whole number the way the rest of the dashboard prints one: Indian grouping. */
export const fmtInt = (n: number): string => n.toLocaleString('en-IN');

// ── Reading the API's answers ────────────────────────────────────────────────
// The API validates and rebuilds every upstream row, so these are not a security
// boundary. They exist so that a field an older build leaves out — or a deploy that
// puts the web ahead of the API for a few minutes — costs one empty cell rather
// than a white screen: nothing below this point has to ask "is it an array?".

/** Counts with every sub-tab present; a key the answer lacks reads as zeros. */
export function readCounts(raw: unknown): UniformCounts {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, Partial<UniformTabCount> | undefined>;
  const out = {} as UniformCounts;
  for (const t of UNIFORM_TABS) {
    const c = src[t.id];
    out[t.id] = c
      ? { pre_joinee: toCount(c.pre_joinee), joined: toCount(c.joined), total: toCount(c.total) }
      : { ...ZERO_COUNT };
  }
  return out;
}

export function readRequests(raw: unknown, tab: UniformTabKey): UniformRequestsData {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Partial<UniformRequestsData>;
  const rows = Array.isArray(src.rows) ? src.rows : [];
  return {
    tab,
    counts: readCounts(src.counts),
    // The API sends only this sub-tab's rows. Dropping one that names ANOTHER
    // sub-tab is free, and means a row can never be drawn under the wrong heading
    // with the wrong columns. A row that names none is kept — it was sent as this
    // sub-tab's, and discarding it would show an empty table under a non-zero count.
    rows: rows.filter((r): r is UniformRequestRow => !!r && typeof r === 'object' && (r.tab == null || r.tab === tab)),
    cap: toCount(src.cap),
    capped: { open: !!src.capped?.open, completed: !!src.capped?.completed },
    fetchedAt: typeof src.fetchedAt === 'string' ? src.fetchedAt : null,
  };
}

export function readSla(raw: unknown): UniformSla | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as UniformSla;
  // Strictly `true`: anything else — absent, null, "yes" — is treated as not
  // measured, because the alternative is printing counts that do not exist.
  return { ...src, measured: src.measured === true };
}

export function readKitStock(raw: unknown): UniformKitStock | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Partial<UniformKitStock>;
  const items = Array.isArray(src.items) ? src.items : [];
  const t = (src.totals ?? {}) as Partial<UniformKitStock['totals']>;
  return {
    kits: Array.isArray(src.kits) ? src.kits : [],
    items,
    // Passed through as number-or-null, never defaulted: the card prints a dash for
    // a total that was not sent (stockValue), which is how the 1Office screen shows it.
    totals: {
      items: toCountOrNull(t.items),
      available: toCountOrNull(t.available),
      zero: toCountOrNull(t.zero),
      low: toCountOrNull(t.low),
    },
  };
}

// ── Errors ───────────────────────────────────────────────────────────────────

export const httpStatus = (err: unknown): number | null =>
  (err as { response?: { status?: number } })?.response?.status ?? null;

export const apiMessage = (err: unknown, fallback: string): string => {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

/**
 * What a failed load means for the whole tab.
 *
 *   denied          403 — the access answer. The grant is checked server-side against
 *                   the project in the URL, so this is the only place the client
 *                   learns it; it is a state of the tab, not an error. `message` is
 *                   the server's own wording, which tells "no grant on this project"
 *                   apart from "could not verify who you are" (also a 403: the API
 *                   never answers 401 here, because the web client reads any 401 as
 *                   "signed out" and would log the user out of the whole app). null
 *                   when the 403 carried none — a proxy's page, say — and the tab
 *                   then falls back to its own wording.
 *   not_configured  503 carrying the API's own error envelope — this server has no
 *                   Admin Management secret. The envelope check matters: a gateway
 *                   in front of a restarting API also answers 503, with an HTML
 *                   page, and telling an admin to go and configure a server that is
 *                   merely redeploying would send them looking for a fault that is
 *                   not there. That one stays an ordinary, retryable error.
 *   error           everything else — shown as a banner with the server's message.
 */
export type UniformLoadFailure =
  | { kind: 'denied'; message: string | null }
  | { kind: 'not_configured'; message: string }
  | { kind: 'error'; message: string };

export function classifyFailure(err: unknown, fallback: string): UniformLoadFailure {
  const status = httpStatus(err);
  const message = apiMessage(err, '');
  if (status === 403) return { kind: 'denied', message: message || null };
  if (status === 503 && message) return { kind: 'not_configured', message };
  return { kind: 'error', message: message || fallback };
}

// ── Text formatting ──────────────────────────────────────────────────────────

/** 'YYYY-MM-DD' → 'dd-mm-yy' as text; '—' when absent; anything else as sent. */
export function fmtYmd(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? `${m[3]}-${m[2]}-${m[1].slice(-2)}` : v;
}

/** The courier's 'YYYY-MM-DD HH:mm[:ss]' → 'dd-mm-yy HH:mm' as text. A zone-less
 *  stamp is read differently by different browsers' Date parsers, so it never goes
 *  near one. Anything else is shown as sent. */
export function fmtScanTime(v: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v || '');
  return m ? `${m[3]}-${m[2]}-${m[1].slice(-2)} ${m[4]}:${m[5]}` : (v || '—');
}

/** 'CODE-Name' when both are known, the name (else the code) otherwise, '—' when
 *  neither — Admin Management's formatCodeName, so a person reads the same on both
 *  screens. Joined: employee code-name. Pre Joinee: Join ID-candidate name. */
export function codeName(code: string | number | null | undefined, name: string | null | undefined): string {
  const c = code != null && String(code).trim() !== '' ? String(code).trim() : '';
  const n = name != null && String(name).trim() !== '' ? String(name).trim() : '';
  if (c && n) return `${c}-${n}`;
  return n || c || '—';
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

/** The SLA card's month from the KPI's 'YYYY-MM' key ('October'), as text. */
export function monthName(key: string | null | undefined): string {
  const m = /^\d{4}-(\d{2})$/.exec(key || '');
  return (m && MONTH_NAMES[Number(m[1]) - 1]) || 'current month';
}

/** "(Pre Joinee X / Joined Y)" under a sub-tab's name. Until the counts load it is
 *  a non-breaking space, so the strip keeps its height instead of jumping. */
export function countLabel(counts: UniformCounts | null, id: UniformTabKey): string {
  if (!counts) return ' ';
  const c = counts[id] ?? ZERO_COUNT;
  return `(Pre Joinee ${fmtInt(c.pre_joinee)} / Joined ${fmtInt(c.joined)})`;
}

// ── TAT and Status badges ────────────────────────────────────────────────────

export type BadgeTone = 'success' | 'danger' | 'neutral';

/* The 3-day TAT label, judged by Admin Management and only from a recorded SLA
   start — the user's submit, or the PM approval of a self-raised request — so a row
   with neither carries no label. Overdue on Courier Pending/Handover; Within SLA /
   Out of TAT once the admin has acted. */
const TAT_LABEL: Record<UniformTat, { text: string; tone: BadgeTone }> = {
  overdue: { text: 'Overdue', tone: 'danger' },
  within: { text: 'Within SLA', tone: 'success' },
  without: { text: 'Out of TAT', tone: 'danger' },
};

/** The hover line behind a TAT badge: how many days, counted from what. */
export function tatHint(r: Pick<UniformRequestRow, 'tat' | 'tat_days' | 'sla_basis' | 'sla_start_on'>): string {
  const days = r.tat_days ?? 0;
  const unit = days === 1 ? 'day' : 'days';
  const from = `${r.sla_basis === 'pm_approval' ? 'PM approval' : 'User Submitted'} (${fmtYmd(r.sla_start_on)})`;
  return r.tat === 'overdue' ? `${days} ${unit} since ${from}` : `Processed ${days} ${unit} after ${from}`;
}

/** A row's TAT badge, or null when Admin Management judged none — including a label
 *  a newer build might add that this screen does not know how to colour. */
export function tatBadge(
  r: Pick<UniformRequestRow, 'tat' | 'tat_days' | 'sla_basis' | 'sla_start_on'>,
): { text: string; tone: BadgeTone; hint: string } | null {
  const label = r.tat ? TAT_LABEL[r.tat] : undefined;
  return label ? { ...label, hint: tatHint(r) } : null;
}

/* The Completed tab's Status column: what the request ended as. The tab lists
   cancelled and rejected requests beside the completed ones, so every row says
   which it is. Tone by request_status — 3 Completed, 5 Cancelled, 6 / 7 Rejected. */
const ENDED_STATUS_TONE: Record<number, BadgeTone> = { 3: 'success', 5: 'neutral', 6: 'danger', 7: 'danger' };

/** A Completed row's Status badge, or null when the answer carries no label (an
 *  older Admin Management build): the cell is then empty, not guessed. */
export function statusBadge(
  r: Pick<UniformRequestRow, 'status_label' | 'request_status'>,
): { text: string; tone: BadgeTone } | null {
  const text = typeof r.status_label === 'string' ? r.status_label.trim() : '';
  return text ? { text, tone: ENDED_STATUS_TONE[r.request_status] ?? 'neutral' } : null;
}

// ── The two cards ────────────────────────────────────────────────────────────

/* The SLA card measures a request the way Admin Management's Timely Closure KPI does,
   for the current month and this project: its percentage, then the Within SLA / Out
   of TAT counts. It is NOT a count of the rows' TAT badges, which judge courier on
   its booking day and need a recorded submit. There is no month filter — the card's
   heading names the month — and the Dashboard's month picker does not move it.

   The hint deliberately does NOT list which requests are counted. That depends on
   the Admin Management build that is live: one build counts only the requests whose
   3-day clock started this month, the next also counts earlier months' requests
   still waiting or processed this month. A sentence spelling either out would be
   wrong on the other build, and someone reconciling the card against the rows would
   be reading a definition that does not match the number. So it points at the KPI
   that owns the definition, and states only what holds on every build. */
export const SLA_HINT = 'This month\'s 3-day SLA for this project, measured the way the Timely Closure KPI of the '
  + '1Office Admin Management Uniform Requests page measures a request. The clock starts at the PM approval of a '
  + 'PM-approved request, else the acknowledgement date (Pre Joinee: the intake submit). Within SLA: closed '
  + 'within 3 days — completed (handed over, or the courier done), or its courier booked. Out of TAT: closed '
  + 'later, or still open past 3 days. Always the current month, whatever the Dashboard\'s Month is set to.';

/* The Kit stock card shows only how many sizes are Low and Out of stock; its Download
   lists every garment's stock by size. The kits are the ones attached to the
   project's active budgets, and the stock is what a request draws from — every
   warehouse — so a garment on two kits is counted once. */
export const KIT_STOCK_HINT = 'Sizes of the garments on the uniform kits attached to this project\'s active budgets, '
  + 'from every warehouse. Low: in stock but at or below its reorder level. Out of stock: nothing to draw, '
  + 'including a size the garment is made in but never received. Download: every garment\'s stock, by size.';

/** The SLA card's hover text. A month that is only partly measured — it started
 *  before the KPI's go-live, or simply is not over yet — also says which days count. */
export function slaHint(sla: UniformSla | null): string {
  return sla?.measured && sla.partial && sla.window?.start && sla.window?.end
    ? `${SLA_HINT}\n\nThis month is counted from ${fmtYmd(sla.window.start)} to ${fmtYmd(sla.window.end)}.`
    : SLA_HINT;
}

/** An SLA count: '…' while loading (the last figures may belong to another project),
 *  '—' when the month is unmeasured or the card failed — never a 0 standing in for
 *  "not measured". */
export function slaValue(sla: UniformSla | null, loading: boolean, n: number | null | undefined): string {
  if (loading) return '…';
  if (!sla?.measured || n == null || !Number.isFinite(n)) return '—';
  return fmtInt(n);
}

/** The Timely Closure %, the same way; '—' also when the month has decided nothing. */
export function slaPct(sla: UniformSla | null, loading: boolean): string {
  if (loading) return '…';
  if (!sla?.measured || sla.timelyPct == null || !Number.isFinite(sla.timelyPct)) return '—';
  return `${sla.timelyPct}%`;
}

/** A Kit stock figure: '…' while loading, '—' when the card failed to load or the
 *  answer carried no readable figure (a null total) — never a 0 in its place. */
export function stockValue(loading: boolean, n: number | null | undefined): string {
  if (loading) return '…';
  if (n == null || !Number.isFinite(n)) return '—';
  return fmtInt(n);
}

// ── The table ────────────────────────────────────────────────────────────────

/** Does a row match the search box? Over what a person would type to find a
 *  request: its code, the employee code / Join ID and name, the budget, and (on
 *  Completed) what it ended as. Dates and TAT are sortable, not searchable. */
export function matchesUniformSearch(r: UniformRequestRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [r.request_code, codeName(r.person_code, r.person_name), r.join_id, r.budget_name, r.status_label]
    .some((v) => v != null && String(v).toLowerCase().includes(q));
}

/**
 * The "you are not seeing everything" line, or null: this sub-tab holds fewer rows
 * than its true count.
 *
 * The count alone decides. Admin Management returns the newest 5,000 open requests
 * and the newest 5,000 Completed ones and says which half overflowed — but that
 * flag is not the only way to come up short: the API also drops a row it cannot
 * read, or one that names another project. Waiting for `capped` would leave a
 * table of 3 rows under a count of 32 with nothing said.
 *
 * `capped` is still reported, for the wording: when this sub-tab's half overflowed,
 * the rows on screen are the NEWEST ones, which is worth saying; when it did not,
 * "newest" would be a guess.
 */
export function rowShortfall(
  tab: UniformTabKey,
  data: Pick<UniformRequestsData, 'rows' | 'capped'> | null | undefined,
  counts: UniformCounts | null,
): { shown: number; total: number; capped: boolean } | null {
  if (!data || !counts) return null;
  const total = counts[tab]?.total ?? 0;
  if (data.rows.length >= total) return null;
  const capped = tab === 'completed' ? data.capped.completed : data.capped.open;
  return { shown: data.rows.length, total, capped };
}

/** The shortfall line as text. */
export function shortfallText(s: { shown: number; total: number; capped: boolean }): string {
  return s.capped
    ? `Showing the newest ${fmtInt(s.shown)} of ${fmtInt(s.total)} requests in this tab.`
    : `Showing ${fmtInt(s.shown)} of ${fmtInt(s.total)} requests in this tab. The rest are not available in this view.`;
}

/**
 * What an empty table says, when it loaded and no search is hiding its rows.
 *
 * The per-tab wording ("No request is waiting for approval.") is a claim about
 * 1Office, so it is made only when the sub-tab's TRUE total is 0. No rows under a
 * non-zero count means the rows were not sent — capped away, or dropped by the API —
 * and saying "no request is waiting" there would contradict the count printed on the
 * strip directly above. `total` is null only if no count is known at all, which is
 * no ground for the claim either.
 */
export function emptyState(tab: UniformTabKey, total: number | null): { title: string; text: string } {
  if (total === 0) {
    return { title: 'No requests', text: UNIFORM_TABS.find((t) => t.id === tab)?.empty ?? '' };
  }
  if (total == null) {
    return { title: 'Rows not available', text: 'The requests of this tab are not available in this view.' };
  }
  return {
    title: 'Rows not available',
    text: total === 1
      ? 'This tab has 1 request, but its row is not available in this view.'
      : `This tab has ${fmtInt(total)} requests, but their rows are not available in this view.`,
  };
}

// ── View kit ─────────────────────────────────────────────────────────────────

const joinDot = (parts: Array<string | null | undefined>): string => parts.filter(Boolean).join(' · ');

/** One kit of a request. `heading` is set only when the request holds several. */
export interface UniformKitSection {
  key: string;
  heading: string | null;
  items: UniformKitLine[];
  ufMode: string | null;
  requestStatus: number | null;
}

/**
 * The View kit modal's header line and sections, from one request's kits.
 *
 * Pre Joinee and Joined kits arrive differently: a candidate's kit carries no name,
 * kit or quarter of its own (they come from the row), and has no courier mode, so
 * its Stock column never marks anything; an employee's kit carries all of them.
 * `kits` being empty, or not an array at all, yields no sections rather than a throw.
 */
export function buildKitView(
  row: Pick<UniformRequestRow, 'request_type' | 'request_status' | 'person_name' | 'offer_kit_name'>,
  kitsRaw: unknown,
): { description: string | null; sections: UniformKitSection[] } {
  const kits = (Array.isArray(kitsRaw) ? kitsRaw : []).filter((k): k is UniformKit => !!k && typeof k === 'object');
  const many = kits.length > 1;
  const lines = (k: UniformKit): UniformKitLine[] => (Array.isArray(k.items) ? k.items : []);
  // dispatch_id alone is not a safe React key if upstream ever repeats one.
  const key = (k: UniformKit, i: number): string => `${k.dispatch_id ?? 'kit'}:${i}`;

  if (row.request_type === -11) {
    return {
      description: joinDot([row.person_name, row.offer_kit_name]) || null,
      sections: kits.map((k, i) => ({
        key: key(k, i),
        heading: many ? `Kit ${i + 1}` : null,
        items: lines(k),
        ufMode: null,
        requestStatus: row.request_status,
      })),
    };
  }

  const first = kits[0];
  return {
    description: (kits.length === 1
      ? joinDot([first.employee_name, first.type_name, first.period_label])
      : joinDot([first?.employee_name ?? row.person_name, many ? `${kits.length} kits` : null])) || null,
    sections: kits.map((k, i) => ({
      key: key(k, i),
      heading: many ? joinDot([k.type_name, k.period_label]) || `Kit ${i + 1}` : null,
      items: lines(k),
      ufMode: k.uf_mode ?? null,
      requestStatus: k.uf_request_status ?? row.request_status,
    })),
  };
}

/**
 * What a kit line's Stock cell says — Admin Management's rule, verbatim.
 *
 * Only a COURIER line that is still to be drawn from stock is marked: the request
 * is at status 2 / -3 / 3, the line is not issued, and it has a size. "Not issued"
 * is a stated `issued === 0`, strictly: a line whose `issued` is null or absent
 * (nothing readable came from upstream) gets no mark at all, as on the 1Office
 * screen — a red "not drawn" there would accuse a parcel of leaving a garment
 * behind on no evidence.
 *   not_drawn          in flight (2, -3): normal, the draw runs at booking/processing.
 *   not_drawn_shipped  the parcel has gone (3) without this garment ever being drawn.
 *   no_stock_item      the line has no stock item, so it can never be drawn — adding
 *                      inventory will not clear it.
 *   none               everything else: a dash.
 */
export type KitStockMark = 'none' | 'no_stock_item' | 'not_drawn' | 'not_drawn_shipped';

export function kitStockMark(
  line: Pick<UniformKitLine, 'item_id' | 'size' | 'issued'>,
  ufMode: string | null,
  requestStatus: number | null,
): KitStockMark {
  const counted = ufMode === 'courier'
    && [2, -3, 3].includes(requestStatus ?? 0)
    && line.issued === 0
    && !!line.size;
  if (!counted) return 'none';
  if (line.item_id == null) return 'no_stock_item';
  return requestStatus === 3 ? 'not_drawn_shipped' : 'not_drawn';
}

// ── Courier status ───────────────────────────────────────────────────────────

/** A uniform kit ships as ONE parcel under one AWB, so the AWBs are de-duplicated —
 *  first seen wins — and an entry with no code (nothing to track) is dropped. */
export function uniqueAwbs(raw: unknown): UniformAwb[] {
  const seen = new Set<string>();
  return (Array.isArray(raw) ? raw : []).filter((a): a is UniformAwb => {
    const code = a && typeof a === 'object' ? String((a as UniformAwb).awb_code ?? '').trim() : '';
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}
